import { useEffect, useState } from "react";
import useAxios from "axios-hooks";
import {
  DefaultButton,
  Dialog,
  DialogFooter,
  DialogType,
  Dropdown,
  MessageBar,
  MessageBarType,
  PrimaryButton,
  Stack,
  Text,
  TextField
} from "@fluentui/react";

const statusColorMap = {
  Expired: "#a4262c",
  ExpiringIn7Days: "#a4262c",
  ExpiringIn30Days: "#8a6f00",
  ExpiringIn90Days: "#876400",
  ExpiringInMoreThan90Days: "#107c10",
  InvalidCertificate: "#a4262c"
};

const formatStatus = (status) => {
  if (!status) {
    return "Unknown";
  }

  switch (status) {
    case "ExpiringIn7Days":
      return "Expiring in 7 days";
    case "ExpiringIn30Days":
      return "Expiring in 30 days";
    case "ExpiringIn90Days":
      return "Expiring in 90 days";
    case "ExpiringInMoreThan90Days":
      return "Valid (90+ days)";
    case "InvalidCertificate":
      return "Invalid certificate";
    default:
      return status;
  }
};

const formatUtcDate = (dateValue) => {
  if (!dateValue || dateValue === "0001-01-01T00:00:00") {
    return "N/A";
  }

  const date = new Date(dateValue);

  if (Number.isNaN(date.getTime())) {
    return "N/A";
  }

  return `${date.toLocaleDateString()} ${date.toLocaleTimeString()} UTC`;
};

const getApiErrorMessage = (error, fallbackMessage) => {
  const responseData = error?.response?.data;
  const errors = responseData?.errors;

  if (Array.isArray(errors) && errors.length > 0) {
    const firstError = errors[0];

    if (typeof firstError === "string") {
      return firstError;
    }

    if (firstError?.errorMessage) {
      return firstError.errorMessage;
    }
  }

  if (typeof responseData === "string") {
    return responseData;
  }

  if (responseData?.message) {
    return responseData.message;
  }

  return fallbackMessage;
};

const certificateTableStyle = { backgroundColor: "#ffffff" };
const certificateTableHeaderCellStyle = { backgroundColor: "#f3f2f1" };
const certificateRowPrimaryStyle = { backgroundColor: "#ffffff" };

const certificateUseOptions = [
  { key: "Signature", text: "Signature" },
  { key: "Encryption", text: "Encryption" },
  { key: "Any", text: "Both" }
];

const AddCertificateDialog = ({ hidden, selectedCobrandId, cobrandIdpId, onDismiss, onAdded }) => {
  const [certificateInput, setCertificateInput] = useState("");
  const [certificateUse, setCertificateUse] = useState("Signature");
  const [parseResult, setParseResult] = useState(null);
  const [dialogError, setDialogError] = useState("");
  const [dialogInfo, setDialogInfo] = useState("");

  const [{ loading: parsingCertificate }, parseCertificate] = useAxios(
    { url: "/internal/cobrand/0/cobrandidp/parse-certificate", method: "POST" },
    { manual: true }
  );
  const [{ loading: addingCertificate }, addCertificate] = useAxios(
    { url: "/internal/cobrand/0/cobrandidp/add-certificate", method: "POST" },
    { manual: true }
  );

  useEffect(() => {
    if (hidden) {
      setCertificateInput("");
      setCertificateUse("Signature");
      setParseResult(null);
      setDialogError("");
      setDialogInfo("");
    }
  }, [hidden]);

  const buildRequestPayload = () => ({
    certificate: certificateInput,
    cobrandIdpId,
    use: certificateUse
  });

  const handleCertificateFileSelected = (event) => {
    const file = event.target?.files?.[0];

    if (!file) {
      return;
    }

    const reader = new FileReader();

    reader.onload = () => {
      setCertificateInput(reader.result?.toString() || "");
      setDialogError("");
      setParseResult(null);
      setDialogInfo(`Loaded ${file.name}.`);
    };

    reader.onerror = () => {
      setDialogError("Unable to read the selected file.");
    };

    reader.readAsText(file);
    event.target.value = "";
  };

  const handlePreviewCertificate = async () => {
    setDialogError("");
    setDialogInfo("");
    setParseResult(null);

    if (!selectedCobrandId) {
      setDialogError("Select a cobrand first.");
      return;
    }

    if (!certificateInput.trim()) {
      setDialogError("Paste a certificate or upload a certificate file first.");
      return;
    }

    try {
      const response = await parseCertificate({
        url: `/internal/cobrand/${selectedCobrandId}/cobrandidp/parse-certificate`,
        data: buildRequestPayload()
      });

      setParseResult(response?.data || null);

      if (response?.data?.alreadyExists) {
        setDialogInfo("This thumbprint is already loaded for this IdP. No update is needed.");
      }
    } catch (error) {
      setDialogError(getApiErrorMessage(error, "Unable to parse certificate."));
    }
  };

  const handleAddCertificate = async () => {
    setDialogError("");
    setDialogInfo("");

    if (!selectedCobrandId) {
      setDialogError("Select a cobrand first.");
      return;
    }

    if (!parseResult) {
      setDialogError("Preview the certificate before adding it.");
      return;
    }

    if (parseResult.alreadyExists) {
      setDialogInfo("Did not add certificate because this thumbprint is already loaded.");
      return;
    }

    try {
      await addCertificate({
        url: `/internal/cobrand/${selectedCobrandId}/cobrandidp/add-certificate`,
        data: buildRequestPayload()
      });

      await onAdded();
      onDismiss();
    } catch (error) {
      setDialogError(getApiErrorMessage(error, "Unable to add certificate."));
    }
  };

  return (
    <Dialog
      hidden={hidden}
      onDismiss={onDismiss}
      minWidth={750}
      maxWidth={975}
      modalProps={{ isBlocking: false }}
      dialogContentProps={{
        type: DialogType.normal,
        title: "Add Certificate",
        subText: "Paste a certificate or upload a file, preview details, then confirm add."
      }}
    >
      <Stack tokens={{ childrenGap: 10 }}>
        {dialogError && (
          <MessageBar messageBarType={MessageBarType.error} isMultiline>
            {dialogError}
          </MessageBar>
        )}

        {dialogInfo && (
          <MessageBar messageBarType={MessageBarType.warning} isMultiline>
            {dialogInfo}
          </MessageBar>
        )}

        <Dropdown
          label="Use"
          selectedKey={certificateUse}
          options={certificateUseOptions}
          onChange={(_, option) => {
            setCertificateUse(option?.key || "Signature");
            setParseResult(null);
          }}
        />

        <Text variant="small">Upload certificate file</Text>
        <input
          type="file"
          accept=".cer,.crt,.pem,.txt"
          onChange={handleCertificateFileSelected}
        />

        <TextField
          label="Certificate"
          multiline
          rows={10}
          value={certificateInput}
          onChange={(_, value) => {
            setCertificateInput(value || "");
            setParseResult(null);
          }}
        />

        {parseResult && (
          <div className="table-responsive">
            <table className="table table-bordered" style={certificateTableStyle}>
              <thead>
                <tr style={certificateRowPrimaryStyle}>
                  <th style={certificateTableHeaderCellStyle}>Use</th>
                  <th style={certificateTableHeaderCellStyle}>Status</th>
                  <th style={certificateTableHeaderCellStyle}>Expires (UTC)</th>
                  <th style={certificateTableHeaderCellStyle}>Thumbprint</th>
                  <th style={certificateTableHeaderCellStyle}>Subject</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>{parseResult.use || "Unknown"}</td>
                  <td>
                    <span style={{ color: statusColorMap[parseResult.status] || "#323130", fontWeight: 600 }}>
                      {formatStatus(parseResult.status)}
                    </span>
                  </td>
                  <td>{formatUtcDate(parseResult.expirationDateUtc)}</td>
                  <td>{parseResult.thumbprint || "N/A"}</td>
                  <td style={{ wordBreak: "break-word", maxWidth: 420 }}>{parseResult.subject || "N/A"}</td>
                </tr>
              </tbody>
            </table>
          </div>
        )}
      </Stack>

      <DialogFooter>
        <DefaultButton
          text={parsingCertificate ? "Previewing..." : "Preview"}
          onClick={handlePreviewCertificate}
          disabled={parsingCertificate || addingCertificate}
        />
        <PrimaryButton
          text={addingCertificate ? "Adding..." : "Add Certificate"}
          onClick={handleAddCertificate}
          disabled={addingCertificate || parsingCertificate || !parseResult || parseResult?.alreadyExists}
        />
        <DefaultButton text="Cancel" onClick={onDismiss} disabled={addingCertificate || parsingCertificate} />
      </DialogFooter>
    </Dialog>
  );
};

export default AddCertificateDialog;
